/**
 * The Learn Codako curriculum.
 *
 * Each lesson opens its own small world - the JSON in ./worlds, built by
 * scripts/build-lesson-worlds.ts - and runs the walkthrough steps registered
 * for its slug in editor/constants/lessons. Lessons run in order, but each one
 * starts from a prebuilt world, so a kid can begin anywhere.
 *
 * This module is imported by the lesson index page, so it deliberately holds no
 * editor code: the worlds are loaded on demand and the steps live elsewhere.
 */
export type Lesson = {
  slug: string;
  title: string;
  /** Shown on the lesson card. */
  caption: string;
  screenshot: string;
  /** Name given to the world this lesson creates, so it reads well in My Games. */
  worldName: string;
  loadWorld: () => Promise<unknown>;
};

const screenshot = (name: string) => new URL(`./screenshots/${name}`, import.meta.url).href;

export const LESSONS: Lesson[] = [
  {
    slug: "playback",
    title: "Play the Game",
    caption: "Start and stop a game, and move the hero with the arrow keys.",
    screenshot: screenshot("playback.jpg"),
    worldName: "Lesson 1: Play the Game",
    loadWorld: () => import("./worlds/playback.json").then((m) => m.default),
  },
  {
    slug: "draw-a-character",
    title: "Draw a Character",
    caption: "Draw a bridge piece in the paint tools and build a path over the lava.",
    screenshot: screenshot("draw-a-character.jpg"),
    worldName: "Lesson 2: Draw a Character",
    loadWorld: () => import("./worlds/draw-a-character.json").then((m) => m.default),
  },
  {
    slug: "record-a-rule",
    title: "Record a Rule",
    caption: "Use the recorder to show the hero how to climb over a boulder.",
    screenshot: screenshot("record-a-rule.jpg"),
    worldName: "Lesson 3: Record a Rule",
    loadWorld: () => import("./worlds/record-a-rule.json").then((m) => m.default),
  },
  {
    slug: "event-blocks",
    title: "Rule Boxes",
    caption: "Put a rule in a rule box that checks for a key, so it only runs when you press it.",
    screenshot: screenshot("event-blocks.jpg"),
    worldName: "Lesson 4: Rule Boxes",
    loadWorld: () => import("./worlds/event-blocks.json").then((m) => m.default),
  },
  {
    slug: "falling-boulder",
    title: "Falling Boulder",
    caption: "Teach a boulder to slip off a ledge, then teach it to fall.",
    screenshot: screenshot("falling-boulder.jpg"),
    worldName: "Lesson 5: Falling Boulder",
    loadWorld: () => import("./worlds/falling-boulder.json").then((m) => m.default),
  },
  {
    slug: "animate-appearances",
    title: "Animate Appearances",
    caption: "Paint a second picture for a bird and use two rules to make it flap across the sky.",
    screenshot: screenshot("animate-appearances.jpg"),
    worldName: "Lesson 6: Animate Appearances",
    loadWorld: () => import("./worlds/animate-appearances.json").then((m) => m.default),
  },
  {
    slug: "horse-race",
    title: "Horse Race",
    caption: "Use a random rule box so nobody knows which horse will win.",
    screenshot: screenshot("horse-race.jpg"),
    worldName: "Lesson 7: Horse Race",
    loadWorld: () => import("./worlds/horse-race.json").then((m) => m.default),
  },
];

export function lessonForSlug(slug: string | undefined | null) {
  return LESSONS.find((l) => l.slug === slug);
}

export function lessonAfter(slug: string) {
  const index = LESSONS.findIndex((l) => l.slug === slug);
  return index === -1 ? undefined : LESSONS[index + 1];
}
